import { z } from 'zod/v4';

/**
 * Payload of the `certified-materials-po-import-completed` notification —
 * published by the certified-materials-management service when an async PO
 * import finishes. Drives the completion email + in-app notification.
 */
export const PoImportCompletedSummarySchema = z.object({
  ordersCreated: z.number().int().nonnegative(),
  ordersUpdated: z.number().int().nonnegative(),
  companiesMapped: z.number().int().nonnegative(),
  totalRows: z.number().int().nonnegative(),
  importedSuccessfully: z.number().int().nonnegative(),
  rowsFailed: z.number().int().nonnegative(),
  rowsWithWarnings: z.number().int().nonnegative(),
});

export const PoImportCompletedErrorSchema = z.object({
  row: z.number().int().nonnegative(),
  orderNumber: z.string().optional(),
  column: z.string().optional(),
  errorCode: z.string().optional(),
  detail: z.string(),
  /**
   * 'error' = the row failed to import; 'warning' = the row imported but needs
   * review (e.g. an unmapped company). Optional/defaulted for back-compat with
   * payloads published before this field existed (treated as 'error').
   */
  severity: z.enum(['error', 'warning']).optional(),
});

export const PoImportCompletedNotificationSchema = z.object({
  importId: z.string().min(1),
  companyId: z.string().min(1),
  userId: z.string().nullable(),
  recipientEmail: z.string().nullable(),
  fileName: z.string(),
  status: z.enum(['completed', 'partial', 'failed']),
  summary: PoImportCompletedSummarySchema,
  errorReportUrl: z.string().nullable(),
  topErrors: z.array(PoImportCompletedErrorSchema),
});

export type PoImportCompletedSummary = z.infer<
  typeof PoImportCompletedSummarySchema
>;
export type PoImportCompletedError = z.infer<
  typeof PoImportCompletedErrorSchema
>;
export type PoImportCompletedNotification = z.infer<
  typeof PoImportCompletedNotificationSchema
>;
